// English — complete master set of UI strings for the GuardAI dashboard.
// Every other locale mirrors these keys. Missing keys in other locales
// fall back to this file (see ../index.ts).
const en = {
  // Language + theme switchers
  "lang.label": "Language",
  "lang.select": "Select language",
  "theme.toggle": "Toggle theme",
  "theme.light": "Light mode",
  "theme.dark": "Dark mode",

  // Brand
  "brand.name": "GuardAI by G-Data Labs",
  "brand.short": "GuardAI",
  "brand.subtitle": "Security Patrol",
  "brand.guardSystem": "Guard System",

  // Consoles / roles
  "console.director": "Director Console",
  "console.admin": "Manager Console",
  "console.supervisor": "Supervisor Console",
  "role.administrator": "Administrator",
  "role.director": "Director",
  "role.supervisor": "Supervisor",
  "role.guard": "Security Guard",

  // Navigation — sidebar
  "nav.dashboard": "Dashboard",
  "nav.liveActivity": "Live Activity",
  "nav.devices": "Devices",
  "nav.schedules": "Schedules",
  "nav.alerts": "Alerts",
  "nav.checkpoints": "Checkpoints",
  "nav.reports": "Reports",
  "nav.reportBuilder": "Report Builder",
  "nav.askAi": "Ask AI",
  "nav.admin": "Admin",
  "nav.settings": "Settings",
  "nav.deploy": "Deploy",
  "nav.scanMap": "Scan Map",
  "nav.users": "User Management",
  "nav.calendar": "Calendar",
  "nav.compliance": "Compliance",
  "nav.officers": "Officers",
  "nav.patrolReports": "Patrol Reports",

  // Common actions / labels
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.submit": "Submit",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.search": "Search",
  "common.filter": "Filter",
  "common.export": "Export",
  "common.clear": "Clear",
  "common.viewAll": "View all",
  "common.back": "Back",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.add": "Add",
  "common.refresh": "Refresh",
  "common.retry": "Retry",
  "common.collapse": "Collapse",
  "common.expand": "Expand",
  "common.loading": "Loading…",
  "common.saving": "Saving…",
  "common.saved": "Saved",
  "common.error": "Error",
  "common.noData": "No data",
  "common.required": "Required",
  "common.optional": "Optional",
  "common.yes": "Yes",
  "common.no": "No",
  "common.all": "All",
  "common.none": "None",
  "common.name": "Name",
  "common.status": "Status",
  "common.location": "Location",
  "common.time": "Time",
  "common.date": "Date",
  "common.actions": "Actions",
  "common.today": "today",

  // Domain nouns
  "domain.scanEvent": "Scan event",
  "domain.scanEvents": "Scan events",
  "domain.checkpoint": "Checkpoint",
  "domain.checkpoints": "Checkpoints",
  "domain.hospital": "Hospital",
  "domain.hospitals": "Hospitals",
  "domain.guard": "Guard",
  "domain.guards": "Guards",
  "domain.device": "Device",
  "domain.devices": "Devices",
  "domain.patrol": "Patrol",
  "domain.security": "Security",
  "domain.shift": "Shift",
  "domain.station": "Station",

  // Search bar
  "search.placeholder": "Search guards, locations, or scans…",

  // Notifications
  "notif.title": "Notifications",
  "notif.empty": "No new notifications",
  "notif.viewAll": "View All Notifications",
  "notif.missedCheckpoint": "Missed Checkpoint Alert",

  // Status labels
  "status.active": "Active",
  "status.inactive": "Inactive",
  "status.online": "Online",
  "status.offline": "Offline",
  "status.pending": "Pending",
  "status.approved": "Approved",
  "status.denied": "Denied",
  "status.flagged": "Flagged",
  "status.completed": "Completed",
  "status.missed": "Missed",
  "status.onDuty": "On Duty",
  "status.offDuty": "Off Duty",

  // Auth / login
  "auth.signIn": "Sign in",
  "auth.signingIn": "Signing in…",
  "auth.signOut": "Sign Out",
  "auth.username": "Username",
  "auth.password": "Password",
  "auth.loginFailed": "Login failed",
  "auth.tagline": "Security guards scan their badge at any station and supervisors see updates instantly on a secure dashboard.",

  // Dashboard home
  "dashboard.title": "Security Patrol Hub",
  "dashboard.subtitle": "Real-time monitoring across every checkpoint",
  "dashboard.totalScans": "Total Scans",
  "dashboard.activeGuards": "Active Guards",
  "dashboard.checkpoints": "Checkpoints",
  "dashboard.openAlerts": "Open Alerts",
  "dashboard.recentActivity": "Recent Activity",
  "dashboard.noActivity": "No recent activity",

  // Live activity
  "live.title": "Live Patrol Activity",
  "live.subtitle": "A real-time feed of every badge scan as it happens",
  "live.empty": "No activity yet",
  "live.waiting": "Waiting for the next scan…",

  // Devices
  "devices.title": "Connected Patrol Devices",
  "devices.subtitle": "Status of every scanning device in the field",
  "devices.lastSeen": "Last seen",
  "devices.battery": "Battery",
  "devices.none": "No devices connected",

  // Schedules
  "schedules.title": "Patrol Schedules",
  "schedules.subtitle": "Assign guards to shifts and checkpoints",
  "schedules.addShift": "Add Shift",
  "schedules.none": "No schedules yet",

  // Alerts
  "alerts.title": "Security Alerts",
  "alerts.subtitle": "Missed checkpoints and flagged events that need attention",
  "alerts.none": "No active alerts",
  "alerts.allClear": "All clear — nothing needs attention.",
  "alerts.acknowledge": "Acknowledge",
  "alerts.resolve": "Resolve",

  // Reports
  "reports.title": "Security Reports",
  "reports.subtitle": "Patrol coverage, scan volume, and integrity analytics",
  "reports.builderTitle": "Report Builder",
  "reports.builderSubtitle": "Design professional reports — no coding required.",
  "reports.generate": "Generate Report",
  "reports.noData": "No data for this report",

  // Ask AI
  "ask.title": "Ask AI",
  "ask.greeting": "How can I help you today?",
  "ask.placeholder": "Ask anything about your patrols, guards, or scans…",
  "ask.send": "Send",
  "ask.thinking": "Thinking…",

  // Admin
  "admin.title": "System Administration",
  "admin.subtitle": "Manage access, approvals, and system health",
  "admin.pendingApplications": "Pending Applications",
  "admin.systemHealth": "System Health",
  "admin.uptime": "Uptime",
  "admin.storage": "Storage",
  "admin.users": "Users",
  "admin.latency": "Latency",

  // User management
  "users.title": "User Management",
  "users.subtitle": "Add, edit, and deactivate platform accounts",
  "users.addUser": "Add User",
  "users.none": "No users found",

  // Settings
  "settings.title": "Settings",
  "settings.subtitle": "Manage your account and preferences",
  "settings.account": "Account",
  "settings.appearance": "Appearance",
  "settings.notifications": "Notifications",
  "settings.language": "Language",

  // Deploy
  "deploy.title": "Deployment Planner",
  "deploy.subtitle": "Plan, capture, and verify checkpoint placement",
  "deploy.plan": "Plan",
  "deploy.capture": "Capture",
  "deploy.verify": "Verify",

  // Scan map
  "map.title": "Scan Map",
  "map.subtitle": "Where badge scans are happening across the facility",
  "map.noScans": "No scans to display",

  // Profile menu
  "profile.account": "Account",
  "profile.settings": "Settings",
  "profile.help": "Help & Support",
  "profile.darkMode": "Dark Mode",
  "profile.viewProfile": "View profile",

  // Footer
  "footer.copyright": "© 2026 G-Data Labs. All rights reserved.",
  "footer.terms": "Terms of Use",
  "footer.privacy": "Privacy Policy",
  // ── Auto-added: full dashboard content translations ──
  "admin.addGuard": "Add Guard",
  "admin.addNewGuard": "Add New Guard",
  "admin.addRoute": "Add Route",
  "admin.alertEmail": "Alert Notification Email",
  "admin.avgResponse": "Avg Response",
  "admin.checkpointTimeout": "Checkpoint Timeout (minutes)",
  "admin.checkpointsCount": "Checkpoints ({n})",
  "admin.colBadge": "Badge",
  "admin.colContact": "Contact",
  "admin.colGuard": "Guard",
  "admin.colLastActive": "Last Active",
  "admin.colRole": "Role",
  "admin.colRoutes": "Routes",
  "admin.deactivatedPreviewDesc": "This is how a deactivated account appears to admins.",
  "admin.deactivatedPreviewTitle": "Deactivated guard preview",
  "admin.difficultyEasy": "Easy",
  "admin.difficultyHard": "Hard",
  "admin.difficultyMedium": "Medium",
  "admin.employeeId": "Employee ID:",
  "admin.errorAddGuard": "Unable to add guard. Please try again.",
  "admin.errorAllFields": "All fields are required.",
  "admin.errorLoadDirectory": "Unable to load guard directory right now.",
  "admin.fieldEmail": "Email",
  "admin.fieldEmergencyContact": "Emergency Contact",
  "admin.fieldEmployeeId": "Emory Employee ID",
  "admin.fieldFullName": "Full Name",
  "admin.fieldPhone": "Phone",
  "admin.fieldRole": "Role",
  "admin.fieldTempPassword": "Temporary Password",
  "admin.gpsThreshold": "GPS Accuracy Threshold (meters)",
  "admin.guardId": "Guard ID:",
  "admin.loadingDirectory": "Loading guard directory…",
  "admin.moreCheckpoints": "+{n} more checkpoints",
  "admin.noGuards": "No guards found. Add your first guard to get started.",
  "admin.noRoutesAssigned": "No routes assigned",
  "admin.notGenerated": "Not generated",
  "admin.passwordPolicy": "Password Policy",
  "admin.passwordPolicyDesc": "Enforce strong passwords",
  "admin.patrolRoutes": "Patrol Routes",
  "admin.placeholderTempPassword": "Set a temporary password",
  "admin.resetSessions": "Reset All Sessions",
  "admin.roleAdmin": "Admin",
  "admin.roleDirector": "Director",
  "admin.roleGuard": "Guard",
  "admin.roleSupervisor": "Supervisor",
  "admin.saveConfiguration": "Save Configuration",
  "admin.searchGuards": "Search guards...",
  "admin.securitySettings": "Security Settings",
  "admin.sessionTimeout": "Session Timeout",
  "admin.sessionTimeoutDesc": "Auto-logout after inactivity",
  "admin.statusDeactivated": "Deactivated",
  "admin.statusLabel": "Status:",
  "admin.statusSuspended": "Suspended",
  "admin.storageUsed": "Storage Used",
  "admin.systemConfiguration": "System Configuration",
  "admin.systemStatus": "System Status",
  "admin.tabBadges": "Badge Generator",
  "admin.tabGuards": "Guard Management",
  "admin.tabRoutes": "Route Management",
  "admin.tabSettings": "System Settings",
  "admin.timeout1Hour": "1 hour",
  "admin.timeout2Hours": "2 hours",
  "admin.timeout30Min": "30 minutes",
  "admin.timeout4Hours": "4 hours",
  "admin.totalScans": "Total Scans",
  "admin.twoFactor": "Two-Factor Authentication",
  "admin.twoFactorDesc": "Require 2FA for all supervisors",
  "admin.viewBadge": "View badge",
  "alerts.acknowledged": "Acknowledged",
  "alerts.activeAlerts": "Active Alerts",
  "alerts.allLocationsHealthy": "All locations healthy",
  "alerts.allPriorities": "All Priorities",
  "alerts.allStatus": "All Status",
  "alerts.allTypes": "All Types",
  "alerts.apiError": "API error",
  "alerts.assignedTo": "Assigned to:",
  "alerts.critical": "Critical",
  "alerts.distanceAway": "{n}m away",
  "alerts.exportTitle": "Export Alerts Data",
  "alerts.filters": "Filters",
  "alerts.guardLabel": "Guard {name}",
  "alerts.high": "High",
  "alerts.invalidScan": "Invalid scan",
  "alerts.issuesDetected": "{n} issues detected",
  "alerts.loadFailed": "Failed to load alerts",
  "alerts.loadFailedHint": "check that the alerts endpoint is deployed.",
  "alerts.loadingAlerts": "Loading alerts...",
  "alerts.low": "Low",
  "alerts.medium": "Medium",
  "alerts.noInvalidScans": "No invalid scans in the latest batch.",
  "alerts.noneMatchFilters": "No alerts found matching your filters",
  "alerts.refreshTitle": "Refresh Alerts",
  "alerts.resolved": "Resolved",
  "alerts.scanExceptions": "Patrol scan exceptions",
  "alerts.scanExceptionsSubtitle": "Invalid scans or API errors coming from field devices",
  "alerts.tryAgain": "Try Again",
  "alerts.typeEmergency": "Emergency",
  "alerts.typeMissedCheckpoint": "Missed Checkpoint",
  "alerts.typeSystem": "System",
  "alerts.unknownGuard": "unknown",
  "ask.accessDenied": "Access Denied",
  "ask.accessDeniedDesc": "The Ask AI feature is restricted to administrators.",
  "ask.askNow": "Ask now",
  "ask.askedTimesOne": "Asked {n} time",
  "ask.askedTimesOther": "Asked {n} times",
  "ask.closeHistory": "Close history",
  "ask.dateAtTime": "{date} at {time}",
  "ask.encryptedPrivate": "Encrypted & Private Analysis",
  "ask.errorMessage": "Sorry, I wasn't able to process that question. This could be due to a connection issue or an unsupported query. Please try rephrasing your question or try again in a moment.",
  "ask.exampleInvalidScans": "Which locations logged the most INVALID scans this week?",
  "ask.exampleMissedScans": "Show me guards who missed 3 or more scans in the last 7 days.",
  "ask.exampleNoScansToday": "List locations that haven't received any scans today.",
  "ask.exampleOvernightScans": "How many scans were recorded overnight between 22:00 and 06:00?",
  "ask.frequentlyAsked": "Frequently Asked",
  "ask.hideSql": "Hide SQL Query",
  "ask.history": "History",
  "ask.inputPlaceholder": "Ask something like “Which locations had the fewest scans yesterday?”",
  "ask.noHistory": "No history yet. Ask a question to get started.",
  "ask.noQueriesMatching": "No queries matching \"{q}\"",
  "ask.rowCountOne": "{n} row",
  "ask.rowCountOther": "{n} rows",
  "ask.searchPlaceholder": "Search past queries...",
  "ask.showSql": "Show SQL Query",
  "ask.subtitle": "Ask about patrol compliance, missed checkpoints, guard performance, or location trends.",
  "ask.tagline": "Grounded in real-time patrol data",
  "ask.thisWeek": "This Week",
  "ask.today": "Today",
  "ask.yesterday": "Yesterday",
  "auth.applyToJoin": "Apply to join our team",
  "auth.email": "Email",
  "auth.enterCredentials": "Enter your credentials to access the system.",
  "auth.needAccess": "Need access to the security system?",
  "deploy.addFloor": "+ Floor",
  "deploy.addUnplannedCheckpoint": "Add Unplanned Checkpoint",
  "deploy.address": "Address",
  "deploy.allSaved": "All saved",
  "deploy.area": "Area",
  "deploy.areaDescription": "Area / description",
  "deploy.areaLabel": "Area:",
  "deploy.areaPlaceholder": "ER Entrance, Admin Suite 200…",
  "deploy.areaSuite": "Area / Suite",
  "deploy.cameraError": "Camera error:",
  "deploy.cancelScan": "Cancel Scan",
  "deploy.captureWalkthrough": "Capture Walkthrough",
  "deploy.capturedProgress": "{done} / {total} captured",
  "deploy.checkpointsCount": "Checkpoints ({n})",
  "deploy.ckStatusCaptured": "captured",
  "deploy.ckStatusInstalled": "installed",
  "deploy.ckStatusPlanned": "planned",
  "deploy.ckStatusVerified": "verified",
  "deploy.clickAnywhereToPlace": "Click anywhere to place marker",
  "deploy.clickFloorPlan": "Click floor plan…",
  "deploy.complete": "Complete!",
  "deploy.completionReport": "Completion Report",
  "deploy.contactLabel": "Contact:",
  "deploy.contactPhone": "Contact Phone",
  "deploy.createAndCapture": "Create & Capture",
  "deploy.createProject": "Create Project",
  "deploy.creating": "Creating…",
  "deploy.dismiss": "dismiss",
  "deploy.editingCheckpoint": "Editing checkpoint",
  "deploy.fileFormats": "PNG, JPG, or PDF",
  "deploy.floor": "Floor",
  "deploy.floorColon": "Floor:",
  "deploy.floorLabel": "Floor:",
  "deploy.floorN": "Floor {n}",
  "deploy.floorPlanAlt": "Floor {n} plan",
  "deploy.generated": "Generated:",
  "deploy.gettingGps": "Getting GPS…",
  "deploy.gpsFailedRetry": "GPS failed — retry",
  "deploy.grabGps": "Grab GPS",
  "deploy.hospitalNameRequired": "Hospital Name *",
  "deploy.initials": "Initials: ______",
  "deploy.installVerify": "Install & Verify",
  "deploy.installedAlt": "installed",
  "deploy.installedVerifiedProgress": "{done} / {total} installed & verified",
  "deploy.locationDetail": "Location Detail",
  "deploy.locationDetailPlaceholder": "Left of main doors, above hand sanitizer…",
  "deploy.locationDetailShort": "Left of main doors…",
  "deploy.locationLabel": "Location:",
  "deploy.markInstalledVerified": "Mark Installed & Verified",
  "deploy.newCheckpoint": "New checkpoint",
  "deploy.newDeploymentProject": "New Deployment Project",
  "deploy.newProject": "New Project",
  "deploy.newUnplannedCheckpoint": "New Unplanned Checkpoint",
  "deploy.noCameraFound": "No camera found",
  "deploy.noCheckpointsHint": "Click \"Place Marker\" then click on the floor plan.",
  "deploy.noCheckpointsYet": "No checkpoints yet.",
  "deploy.noProjectSelected": "No project selected",
  "deploy.noProjectSelectedHint": "Select an existing project or create a new one above.",
  "deploy.noProjectsYet": "No projects yet.",
  "deploy.notYetVerified": "Not Yet Verified",
  "deploy.notes": "Notes",
  "deploy.notesLabel": "Notes:",
  "deploy.notesPlaceholder": "Landmark notes, mounting details…",
  "deploy.placeCardHere": "Place the card HERE (from capture):",
  "deploy.placeMarker": "Place Marker",
  "deploy.placementNotes": "Placement Notes",
  "deploy.placementNotesPlaceholder": "5ft high, brick wall, permanent adhesive…",
  "deploy.placementReferenceAlt": "placement reference",
  "deploy.print": "Print",
  "deploy.printChecklist": "Print Checklist",
  "deploy.qrPlaced": "QR placed",
  "deploy.qrPrefix": "QR:",
  "deploy.recapture": "Re-capture",
  "deploy.retakeInstalledPhoto": "Retake Installed Photo",
  "deploy.retakePhoto": "Retake Photo",
  "deploy.reverify": "Re-verify",
  "deploy.saveCapture": "Save Capture",
  "deploy.scanAgain": "Scan Again",
  "deploy.scanQrCode": "Scan QR Code",
  "deploy.scanToVerify": "Scan to Verify",
  "deploy.scanVerified": "Scan verified",
  "deploy.scannedPrefix": "Scanned:",
  "deploy.selectProject": "Select a project…",
  "deploy.selectProjectShort": "Select project…",
  "deploy.selectProjectToCapture": "Select a project to start capturing.",
  "deploy.selectProjectToVerify": "Select a project to start verifying.",
  "deploy.simulation3d": "3D Simulation",
  "deploy.siteContactName": "Site Contact Name",
  "deploy.speechNotSupported": "Speech recognition not supported in this browser.",
  "deploy.statusCapturing": "capturing",
  "deploy.statusComplete": "complete",
  "deploy.statusFabricating": "fabricating",
  "deploy.statusInstalling": "installing",
  "deploy.statusPlanning": "planning",
  "deploy.stop": "Stop",
  "deploy.stopNumber": "Stop #{n}",
  "deploy.stopVoice": "Stop",
  "deploy.takeInstalledPhoto": "Take Installed Photo",
  "deploy.takePhoto": "Take Photo",
  "deploy.total": "Total",
  "deploy.unnamed": "Unnamed",
  "deploy.unnamedLower": "unnamed",
  "deploy.unsavedChanges": "Unsaved changes...",
  "deploy.uploadFloorPlan": "Upload Floor Plan",
  "deploy.uploadFloorPlanFor": "Upload a floor plan for Floor {n}",
  "deploy.uuidMatched": "UUID matched! QR verified.",
  "deploy.uuidMismatch": "UUID mismatch. Wrong QR card.",
  "deploy.uuidPrefix": "UUID:",
  "deploy.verifiedCheckpoints": "Verified Checkpoints",
  "deploy.verifiedCount": "Verified",
  "deploy.voice": "Voice",
  "deploy.walkthroughChecklistStops": "Deployment Walkthrough Checklist — {n} stops",
  "devices.appVersion": "App Version",
  "devices.deviceManagement": "Device Management",
  "devices.emptyHint": "Connected devices will appear here once they send telemetry.",
  "devices.geofences": "Geofences",
  "devices.hoursAgo": "{n}h ago",
  "devices.justNow": "Just now",
  "devices.lastLocation": "Last Location",
  "devices.locateOnMap": "Locate Device on Map",
  "devices.minutesAgo": "{n}m ago",
  "devices.never": "Never",
  "devices.noDevicesFound": "No devices found",
  "devices.noGpsData": "No GPS data",
  "devices.refreshStatus": "Refresh Status",
  "devices.violations": "Violations",
  "geofence.acknowledge": "Acknowledge",
  "geofence.acknowledged": "Acknowledged",
  "geofence.acknowledging": "Acknowledging...",
  "geofence.actionHeader": "Action",
  "geofence.allGeofences": "All Geofences",
  "geofence.create": "Create Geofence",
  "geofence.createdBy": "Created By",
  "geofence.createdDate": "Created Date",
  "geofence.deleteConfirmBody": "This action cannot be undone. The geofence will be permanently deleted.",
  "geofence.deleteConfirmTitle": "Delete Geofence?",
  "geofence.drawPolygon": "Draw Polygon on Map",
  "geofence.drawPolygonHint": "Click on the map to add points. Polygon must have at least 3 points. Current points: {n}",
  "geofence.edit": "Edit Geofence",
  "geofence.enterName": "Please enter a geofence name",
  "geofence.failedCreate": "Failed to create geofence",
  "geofence.failedDelete": "Failed to delete geofence",
  "geofence.failedLoad": "Failed to load geofences",
  "geofence.failedSave": "Failed to save geofence",
  "geofence.failedUpdate": "Failed to update geofence",
  "geofence.filterByGeofence": "Filter by Geofence",
  "geofence.geofenceHeader": "Geofence",
  "geofence.gpsCoordinates": "GPS Coordinates",
  "geofence.hoursAgo": "{n}h ago",
  "geofence.justNow": "Just now",
  "geofence.management": "Geofence Management",
  "geofence.managementSubtitle": "Define secure zones and monitor boundary violations",
  "geofence.minPoints": "Polygon must have at least 3 points",
  "geofence.minutesAgo": "{n}m ago",
  "geofence.nameLabel": "Geofence Name",
  "geofence.namePlaceholder": "e.g., Main Entrance, Parking Lot A",
  "geofence.noViolations": "No violations detected",
  "geofence.noViolationsHint": "All devices are within their designated geofences.",
  "geofence.noneConfigured": "No geofences are configured for your location.",
  "geofence.noneYet": "No geofences yet",
  "geofence.noneYetCanCreate": "Create your first geofence to get started with boundary monitoring.",
  "geofence.pointN": "Point {n}:",
  "geofence.polygonPoints": "Polygon Points ({n})",
  "geofence.preview": "Preview",
  "geofence.save": "Save Geofence",
  "geofence.selectHospitalAlert": "Please select a hospital",
  "geofence.selectHospitalOption": "Select a hospital...",
  "geofence.unacknowledged": "Unacknowledged",
  "geofence.unacknowledgedCount": "{n} unacknowledged",
  "geofence.violationsSubtitle": "Monitor and acknowledge boundary violations",
  "geofence.violationsTitle": "Geofence Violations",
  "help.callNow": "Call now",
  "help.comingSoon": "Coming soon",
  "help.docsComingSoon": "Documentation coming soon.",
  "help.emailSupport": "Email support",
  "help.escalateIssue": "Escalate issue",
  "help.guardHeadline": "Guard help & support",
  "help.guidesFaq": "Guides & FAQ",
  "help.incidentEscalation": "Incident escalation",
  "help.incidentEscalationDesc": "Report outages, kiosk failures, or urgent scan issues.",
  "help.intro": "Get in touch with the Emory Security Patrol team or review quick troubleshooting steps.",
  "help.phoneHotline": "Phone hotline",
  "help.phoneHotlineValue": "(404) 555-0199 • 24/7",
  "help.quickTips": "Quick tips",
  "help.sendEmail": "Send email",
  "help.supervisorHeadline": "Supervisor help & support",
  "help.support": "Support",
  "help.tipAskAi": "Use the Ask AI page to pull historical data before contacting support.",
  "help.tipRefresh": "Refresh the page if scans appear delayed or stale.",
  "help.tipReinstall": "Guards can reinstall the PWA via the Settings page when the icon disappears.",
  "home.activeCheckpoints": "Active Checkpoints",
  "home.activeCheckpointsTooltip": "Total number of physical QR code locations currently registered at this hospital.",
  "home.addNewMarker": "Add New Marker",
  "home.allHospitalsAggregated": "All Hospitals (Aggregated)",
  "hospitals.allAggregated": "All Hospitals (Aggregated)",
  "home.building": "Building",
  "home.buildingFloor": "{building} - Floor {floor}",
  "home.cancelPlacement": "Cancel Placement",
  "home.chartScans": "Scans:",
  "home.checkpointName": "Checkpoint Name",
  "home.checkpointNamePlaceholder": "e.g. Finance (West)",
  "home.checkpointsScanned": "Checkpoints Scanned",
  "home.chooseCheckpoint": "— Choose a checkpoint —",
  "home.cleared": "CLEARED",
  "home.clickToPlace": "Click anywhere on the map to place a new marker.",
  "home.comparingScanVolume": "Comparing active scan volume across all registered sites",
  "home.comparisonVsMapNote": "Counts include every recorded scan. The Scan Map plots only scans with GPS coordinates, so its total may be lower.",
  "home.complianceRate": "Compliance Rate",
  "home.complianceRateTooltip": "Percentage of registered checkpoints that have been scanned at least once during the current shift.",
  "home.complianceRateValue": "Compliance Rate: {n}%",
  "home.confirmDeleteCheckpoint": "Are you sure you want to delete this checkpoint? This action cannot be undone.",
  "home.createCustomCheckpoint": "+ Create custom checkpoint...",
  "home.deleteMarker": "Delete Marker",
  "home.dragToReposition": "Drag markers to reposition. Changes save automatically.",
  "home.editModeOn": "Edit Mode ON",
  "home.editPositions": "Edit Positions",
  "home.failedDeleteLocation": "Failed to delete location",
  "home.failedSaveLocation": "Failed to save location",
  "home.floor": "Floor",
  "home.floorAbbr": "Fl {n}",
  "home.floorN": "Floor {n}",
  "home.headerSubtitle": "Real-time patrol verification and hub upload status",
  "home.hospitalPerformanceComparison": "Hospital Performance Comparison",
  "home.hospitalSecurity": "Hospital Security",
  "home.hub": "Hub: {hub}",
  "home.internalServerError": "Internal Server Error",
  "home.lastHour": "Last Hour",
  "home.lastScan": "Last Scan",
  "home.lastUpdated": "Last updated:",
  "home.legendError": "Error",
  "home.legendInvalid": "Invalid",
  "home.legendOk": "OK",
  "home.liveSystem": "Live System",
  "home.loadingCheckpoints": "Loading checkpoints...",
  "home.loadingComplianceData": "Loading compliance data...",
  "home.loadingUploads": "Loading uploads...",
  "home.mapLast30Days": "Last 30 Days",
  "home.mapLast7Days": "Last 7 Days",
  "home.mapLastQuarter": "Last Quarter",
  "home.mapSubtitle": "Real-time patrol tracking and checkpoint status",
  "home.mapTitle": "Hospital Live Activity Map",
  "home.mapTodaysScans": "Today's Scans",
  "home.moreCheckpoints": "+ {n} more checkpoints",
  "home.noCheckpointsConfigured": "No checkpoints configured for this hospital.",
  "home.noCheckpointsLoaded": "No checkpoints loaded. Check database connection or add one manually below.",
  "home.noUploadsToday": "No uploads recorded today.",
  "home.numberOfScans": "Number of Scans",
  "home.pendingClear": "PENDING CLEAR",
  "home.placeCheckpoint": "Place Checkpoint",
  "home.positionLabel": "Position:",
  "home.qrPayload": "QR Payload",
  "home.qrPayloadPlaceholder": "e.g. FINANCE_WEST",
  "home.recentPatrolActivity7Days": "Recent Patrol Activity (7&nbsp;days)",
  "home.recentScans7Days": "Recent Scans (7&nbsp;days)",
  "home.recentScansTooltip": "Total number of patrol scans successfully uploaded and verified in the last 7 days.",
  "home.registeredCheckpoints": "Registered Checkpoints",
  "home.roleDashboard": "{role} Dashboard:",
  "home.saveCheckpoint": "Save Checkpoint",
  "home.scanner": "Scanner: {serial}",
  "home.scansCount": "{n} scans",
  "home.selectCheckpoint": "Select Checkpoint",
  "home.tooltipPosition": "Position",
  "home.unknownHospital": "Unknown Hospital",
  "home.uploadsByShift": "Uploads by Shift",
  "home.viewingHospital": "Viewing Hospital:",
  "landing.addressInfo": "Address Information",
  "landing.agreeTerms": "I agree to the terms of service",
  "landing.agreeTermsDetail": "I understand that access to the security patrol system is contingent upon supervisor approval and that all information provided is accurate and complete.",
  "landing.availableShifts": "Available Shifts (select all that apply)",
  "landing.bulletIntegrity": "Integrity analytics for missed scans and flagged events.",
  "landing.bulletMonitoring": "Real-time monitoring for every lobby, entrance, and corridor.",
  "landing.bulletWorkspace": "Unified supervisor workspace with instant search and AI insights.",
  "landing.city": "City",
  "landing.completeAndAgree": "Please complete all required fields and agree to the terms.",
  "landing.confirmPassword": "Confirm Password",
  "landing.confirmYourPassword": "Confirm your password",
  "landing.createPassword": "Create a secure password",
  "landing.dateOfBirth": "Date of Birth",
  "landing.emailAddress": "Email Address",
  "landing.emailRegistered": "This email address is already registered.",
  "landing.emailRegisteredUseDifferent": "This email address is already registered. Please use a different email address.",
  "landing.emergencyContact": "Emergency Contact",
  "landing.emergencyContactName": "Emergency Contact Name",
  "landing.emergencyContactPhone": "Emergency Contact Phone",
  "landing.fillRequiredContinue": "Please fill in all required fields before continuing.",
  "landing.firstName": "First Name",
  "landing.lastName": "Last Name",
  "landing.next": "Next",
  "landing.passwordsNoMatch": "Passwords do not match",
  "landing.percentComplete": "{p}% Complete",
  "landing.personalInfo": "Personal Information",
  "landing.phoneNumber": "Phone Number",
  "landing.phoneRegistered": "This phone number is already registered.",
  "landing.phoneRegisteredUseDifferent": "This phone number is already registered. Please use a different phone number.",
  "landing.relChild": "Child",
  "landing.relFriend": "Friend",
  "landing.relOther": "Other",
  "landing.relParent": "Parent",
  "landing.relSibling": "Sibling",
  "landing.relSpouse": "Spouse",
  "landing.relationship": "Relationship",
  "landing.requestAccessSubtitle": "Complete this form to request access to the security patrol system.",
  "landing.requestAccessTitle": "Request System Access",
  "landing.requestSubmitted": "Account Request Submitted",
  "landing.requestSubmittedApproval": "You will be able to log in with your credentials once approved. This process typically takes 1-2 business days.",
  "landing.requestSubmittedThanks": "Thank you for requesting access to the security patrol system. Your request has been submitted and is pending approval by supervisors.",
  "landing.requestedRole": "Requested Role",
  "landing.returnHome": "Return to Home",
  "landing.returnToLogin": "Return to Login",
  "landing.roleAvailability": "Role and Availability",
  "landing.securityDirector": "Security Director",
  "landing.securitySupervisor": "Security Supervisor",
  "landing.selectEllipsis": "Select...",
  "landing.shiftDay": "Day Shift (6am-2pm)",
  "landing.shiftEvening": "Evening Shift (2pm-10pm)",
  "landing.shiftHolidays": "Holidays",
  "landing.shiftNight": "Night Shift (10pm-6am)",
  "landing.shiftOnCall": "On-call",
  "landing.shiftWeekends": "Weekends",
  "landing.signInSubheading": "Use your supervisor or guard credentials to continue.",
  "landing.signInToGuard": "Sign in to Guard",
  "landing.somethingWrong": "Something went wrong.",
  "landing.state": "State",
  "landing.stepOf": "Step {n} of 4",
  "landing.streetAddress": "Street Address",
  "landing.submitApplication": "Submit Application",
  "landing.submitFailed": "Failed to submit application. Please try again.",
  "landing.submitting": "Submitting...",
  "landing.termsOfService": "Terms of Service",
  "landing.zipCode": "ZIP Code",
  "live.action": "Action",
  "live.activityFeed": "Activity Feed",
  "live.asOf": "As of {time}",
  "live.colCoordinate": "Coordinate",
  "live.colDateTime": "Date & time",
  "live.colOfficer": "Officer",
  "live.unattributed": "Unattributed",
  "live.colDistance": "Distance",
  "live.deviceLocation": "(Device Location)",
  "live.filterAll": "All activity",
  "live.filterInvalid": "Invalid scans",
  "live.filterValid": "Valid scans",
  "live.floor": "Floor {n}",
  "live.freshFix": "(Fresh Fix)",
  "live.loadingActivity": "Loading patrol activity…",
  "live.metersAway": "{n}m away",
  "live.noGpsData": "No GPS data",
  "live.noScansFilter": "No scans matching this filter.",
  "live.syncData": "Sync Data",
  "live.timeframe": "Timeframe",
  "live.unknownHospital": "Unknown Hospital",
  "live.unknownLocation": "Unknown Location",
  "live.view3d": "3D View",
  "live.view3dWorld": "View 3D world of this checkpoint",
  "map.allHospitals": "All hospitals",
  "map.autoRefresh": "Auto-refresh",
  "map.autoRefreshTitle": "Auto-refresh every 30s",
  "map.dark": "Dark",
  "map.expandLegend": "Expand legend",
  "map.gpsDisclaimer": "Scan positions reflect device GPS accuracy. Indoor signals may vary by 10–50m from actual location.",
  "map.heatmap": "Heatmap",
  "map.heatmapTitle": "Toggle density heatmap",
  "map.legend": "Legend",
  "map.light": "Light",
  "map.limitedWarning": "Map displays up to {max} scans max. Please narrow your time range or hospital filter to see specific data.",
  "map.loadFailed": "Failed to load scans",
  "map.loadingScans": "Loading scans…",
  "map.mapStyle": "Map style: {name}",
  "map.markers": "Markers",
  "map.minimizeLegend": "Minimize legend",
  "map.noGpsScans": "No GPS-tagged scans found for this time range.",
  "map.pageSubtitle": "Live geographic view of where scan events occurred.",
  "map.pageTitle": "GuardAI Scan Map",
  "map.popupHospital": "Hospital:",
  "map.popupScanner": "Scanner:",
  "map.popupTime": "Time:",
  "map.rangeCustom": "Custom range",
  "map.rangeLast24h": "Last 24h",
  "map.rangeLast30d": "Last 30 days",
  "map.rangeLast7d": "Last 7 days",
  "map.rangeLastHour": "Last hour",
  "map.showingMaxScans": "Showing {shown} (max) of {total} scans",
  "map.snapToBuilding": "Snap to building",
  "map.snapToBuildingTitle": "Snap GPS dots toward building centers to counter indoor GPS drift",
  "map.switchToDark": "Switch to dark map",
  "map.switchToLight": "Switch to light map",
  "map.to": "to",
  "map.trailNote": "Dots are colored by scan time. No path between scans is drawn \u2014 only recorded scan locations are shown.",
  "map.totalScans": "{total} scans",
  "map.unknownHospital": "Unknown",
  "map.updatedAt": "Updated {time}",
  "map.zoomToHospital": "Zoom the map to this location",
  "map.showAllHospitals": "Show all hospitals",
  "reportBuilder.addWidget": "Add a widget",
  "reportBuilder.aiAssistant": "AI report assistant",
  "reportBuilder.aiPlaceholder": "Describe the report you want — e.g. “scan counts by hospital for the last month with a bar chart.” Or sketch a layout on paper and upload a photo.",
  "reportBuilder.aiSummaryHint": "Click “Generate” to write observations from your report’s data.",
  "reportBuilder.allHospitals": "All hospitals",
  "reportBuilder.allTime": "All time",
  "reportBuilder.assistantUnavailable": "The assistant is unavailable right now.",
  "reportBuilder.author": "Author",
  "reportBuilder.beta": "Beta",
  "reportBuilder.buildItForMe": "Build it for me",
  "reportBuilder.buildTogether": "Let's build it together",
  "reportBuilder.canvasEmpty": "Your canvas is empty",
  "reportBuilder.canvasEmptyHint": "Add a widget from the toolbar above, click a data field on the left, or start from a template.",
  "reportBuilder.captionPlaceholder": "Caption (optional)",
  "reportBuilder.color": "Color",
  "reportBuilder.compareAgainst": "Compare against",
  "reportBuilder.configure": "Configure",
  "reportBuilder.copySuffix": "(copy)",
  "reportBuilder.couldntSave": "Couldn’t save:",
  "reportBuilder.dataFields": "Data fields",
  "reportBuilder.dataFieldsHint": "Click any field to drop a pre-built widget onto your report.",
  "reportBuilder.dataSource": "Data source",
  "reportBuilder.dateFrom": "From {date}",
  "reportBuilder.dateFromLabel": "Date from",
  "reportBuilder.dateThrough": "Through {date}",
  "reportBuilder.dateToLabel": "Date to",
  "reportBuilder.dayOfWeekOpt": "Day of week",
  "reportBuilder.describeOrSketch": "Describe the report you want, or attach a sketch.",
  "reportBuilder.dismiss": "Dismiss",
  "reportBuilder.done": "Done",
  "reportBuilder.donutStyle": "Donut style",
  "reportBuilder.draftedOffline": " · drafted offline",
  "reportBuilder.duplicate": "Duplicate",
  "reportBuilder.example1": "Scan counts by hospital for the last month, with a bar chart",
  "reportBuilder.example2": "Daily patrol activity with peak hours and active devices",
  "reportBuilder.example3": "Checkpoint coverage: scanned vs unscanned by hospital",
  "reportBuilder.exportCsv": "CSV (data)",
  "reportBuilder.exportFailed": "Export failed:",
  "reportBuilder.exportHtml": "HTML (shareable)",
  "reportBuilder.exportPdf": "PDF (print-ready)",
  "reportBuilder.exportPng": "PNG image",
  "reportBuilder.exportPrint": "Print",
  "reportBuilder.exportSlides": "Slides-ready (HTML)",
  "reportBuilder.exportTryPdf": "Try PDF (Print) instead.",
  "reportBuilder.exportXls": "Excel (.xls)",
  "reportBuilder.fieldTitle": "Title",
  "reportBuilder.filterFloor": "Filter: floor",
  "reportBuilder.filterHospital": "Filter: hospital",
  "reportBuilder.floorPlaceholder": "e.g. L1, 5, Plaza",
  "reportBuilder.generate": "Generate",
  "reportBuilder.generateBtn": "Generate",
  "reportBuilder.generatedBy": "Generated by GuardAI · © {year} G-Data Labs. All rights reserved.",
  "reportBuilder.generating": "Generating…",
  "reportBuilder.group": "Group",
  "reportBuilder.groupBy": "Group by",
  "reportBuilder.hideSettings": "Hide settings",
  "reportBuilder.horizontal": "horizontal",
  "reportBuilder.imageTooLarge": "That image is a bit large — please use one under 6 MB.",
  "reportBuilder.imageUrlPlaceholder": "Image URL (optional)",
  "reportBuilder.includeLogo": "Include GuardAI logo",
  "reportBuilder.loadFailed": "Couldn’t load data.",
  "reportBuilder.lookAt": "Look at the {target}",
  "reportBuilder.maxRows": "Max rows",
  "reportBuilder.metric": "Metric",
  "reportBuilder.moreSettings": "More settings",
  "reportBuilder.moveDown": "Move down",
  "reportBuilder.moveUp": "Move up",
  "reportBuilder.next": "Next",
  "reportBuilder.noDataSelection": "No data for this selection",
  "reportBuilder.noImageUploaded": "No image uploaded",
  "reportBuilder.noLayout": "I couldn't turn that into a layout — try describing the metrics and how to group them.",
  "reportBuilder.noSummary": "No summary generated.",
  "reportBuilder.noWidgetsYet": "This report has no widgets yet.",
  "reportBuilder.optionalSubtitle": "Optional subtitle",
  "reportBuilder.orientation": "Orientation",
  "reportBuilder.period": "Period: {range}",
  "reportBuilder.preparedBy": "Prepared by {name}",
  "reportBuilder.preview": "Preview",
  "reportBuilder.previewHint": "This is how your report will export and print. Use the Export button above to download.",
  "reportBuilder.regenerate": "Regenerate",
  "reportBuilder.remove": "Remove",
  "reportBuilder.removeImage": "Remove image",
  "reportBuilder.reportImage": "Report image",
  "reportBuilder.reportTitle": "Report title",
  "reportBuilder.reportsAnalytics": "Reports & Analytics",
  "reportBuilder.saveAsTemplate": "Save current report as a shared template",
  "reportBuilder.savedAt": "Saved at {time}",
  "reportBuilder.savedReports": "Saved reports",
  "reportBuilder.secondSeriesLabel": "Second series label",
  "reportBuilder.secondSeriesMetric": "Second series metric",
  "reportBuilder.secondSeriesSource": "Second series source",
  "reportBuilder.selectWidget": "Select a widget to configure it.",
  "reportBuilder.seriesB": "Series B",
  "reportBuilder.sketch": "Sketch",
  "reportBuilder.sketchAlt": "sketch",
  "reportBuilder.sort": "Sort",
  "reportBuilder.sortLabelAsc": "Name, A → Z",
  "reportBuilder.sortLabelDesc": "Name, Z → A",
  "reportBuilder.sortValueAsc": "Value, low → high",
  "reportBuilder.sortValueDesc": "Value, high → low",
  "reportBuilder.startFromTemplate": "Start from a template",
  "reportBuilder.startNewReport": "Start a new report",
  "reportBuilder.stepOf": "Step {n} of {total}",
  "reportBuilder.subtitle": "Subtitle",
  "reportBuilder.summaryFailed": "Failed to generate summary.",
  "reportBuilder.targetAiBar": "AI assistant bar",
  "reportBuilder.targetCanvas": "report canvas",
  "reportBuilder.targetConfigPanel": "configure panel",
  "reportBuilder.targetDataFields": "Data fields panel",
  "reportBuilder.targetExport": "Export button",
  "reportBuilder.targetPreview": "Preview toggle",
  "reportBuilder.targetSave": "Save button",
  "reportBuilder.targetTemplates": "Templates button",
  "reportBuilder.targetToolbar": "widget toolbar",
  "reportBuilder.templates": "Templates",
  "reportBuilder.textPlaceholder": "Write your text. Use ## for a heading and **bold** for emphasis.",
  "reportBuilder.thinking": "Thinking…",
  "reportBuilder.title": "Report Builder",
  "reportBuilder.total": "Total",
  "reportBuilder.try": "Try:",
  "reportBuilder.untitledReport": "Untitled Report",
  "reportBuilder.uploadSketchTitle": "Upload a photo of a hand-drawn report layout",
  "reportBuilder.vertical": "vertical",
  "reportBuilder.walkMeThrough": "Walk me through it",
  "reportBuilder.widgetCountMany": "{n} widgets",
  "reportBuilder.widgetCountOne": "{n} widget",
  "reportBuilder.width": "Width",
  "reportBuilder.widthFull": "Full",
  "reportBuilder.working": "Working…",
  "reports.activeGuards": "Active Guards",
  "reports.asOfTime": "(as of {time})",
  "reports.averageCompliance": "Average Compliance",
  "reports.averageResponseTime": "Average Response Time",
  "reports.checkpointLocationAnalysis": "Checkpoint Location Analysis",
  "reports.checkpointsCompleted": "Checkpoints Completed",
  "reports.checkpointsLoggedGps": "({n} checkpoints logged from GPS data)",
  "reports.colCompliance": "Compliance",
  "reports.colPerformance": "Performance",
  "reports.compliance90to94": "90-94% Compliance",
  "reports.compliance95": "≥95% Compliance",
  "reports.complianceBelow90": "<90% Compliance",
  "reports.complianceLabel": "compliance",
  "reports.complianceReport": "Compliance Report",
  "reports.coverageComplianceHelp": "% of checkpoints visited at least once during this timeframe",
  "reports.coverageComplianceTrends": "Coverage Compliance Trends",
  "reports.criticalIssuesResolved": "Critical Issues Resolved",
  "reports.currentTime": "Current: {time}",
  "reports.dayFri": "Fri",
  "reports.dayFriday": "Friday",
  "reports.dayMon": "Mon",
  "reports.dayMonday": "Monday",
  "reports.daySat": "Sat",
  "reports.daySaturday": "Saturday",
  "reports.daySun": "Sun",
  "reports.daySunday": "Sunday",
  "reports.dayThu": "Thu",
  "reports.dayThursday": "Thursday",
  "reports.dayTue": "Tue",
  "reports.dayTuesday": "Tuesday",
  "reports.dayWed": "Wed",
  "reports.dayWednesday": "Wednesday",
  "reports.deltaLastMonth": "+1.8% from last month",
  "reports.deltaLastQuarter": "+3.2% from last quarter",
  "reports.deltaLastWeek": "+2.1% from last week",
  "reports.deltaYesterday": "+1.5% from yesterday",
  "reports.downloadReport": "Download Report",
  "reports.exceptions": "Exceptions",
  "reports.exportFormat": "Export Format",
  "reports.exportReport": "Export Report",
  "reports.guardPerformanceAnalysis": "Guard Performance Analysis",
  "reports.heatmapCellTitle": "{location} - {label}: {pct}% activity",
  "reports.lessActivity": "Less Activity",
  "reports.loadingPatrolActivity": "Loading patrol activity…",
  "reports.locationAbbreviations": "Location Abbreviations:",
  "reports.locationActivityHeatmap": "Location Activity Heatmap",
  "reports.missedCheckpoints": "Missed Checkpoints",
  "reports.missedCount": "{n} missed",
  "reports.monFeb": "Feb",
  "reports.monJan": "Jan",
  "reports.monMar": "Mar",
  "reports.monthN": "Month {n}",
  "reports.moreActivity": "More Activity",
  "reports.noChange": "No change",
  "reports.noCoverageData": "No coverage data yet",
  "reports.noDataForPeriod": "No data for period",
  "reports.noScanLocations": "No QR code scan locations available",
  "reports.notAvailable": "N/A",
  "reports.overallComplianceRate": "Overall Compliance Rate",
  "reports.patrolLocations": "Patrol locations",
  "reports.periodMonth": "This Month",
  "reports.periodQuarter": "This Quarter",
  "reports.periodToday": "Today",
  "reports.periodWeek": "This Week",
  "reports.popupCompliance": "Compliance:",
  "reports.popupLast": "Last:",
  "reports.popupScans": "Scans:",
  "reports.qrScanLocations": "QR Code Scan Locations",
  "reports.quickExportOptions": "Quick Export Options",
  "reports.realScanData": "Real scan data (last 24h)",
  "reports.scanToSeeLocations": "Scan QR codes to see locations on the map",
  "reports.scansCount": "{n} scans",
  "reports.scansThisMonth": "{n} scans this month",
  "reports.scansThisQuarter": "{n} scans this quarter",
  "reports.scansThisWeek": "{n} scans this week",
  "reports.scansToday": "{n} scans today",
  "reports.tabCompliance": "Compliance",
  "reports.tabGuardPerformance": "Guard Performance",
  "reports.tabLocationAnalysis": "Location Analysis",
  "reports.tabOverview": "Overview",
  "reports.totalScans": "Total scans",
  "reports.trackingLocationsGuards": "Tracking {locations} locations and {guards} guards.",
  "reports.trendDaily": "Daily Performance Trends",
  "reports.trendHourly": "Hourly Performance Trends",
  "reports.trendMonthly": "Monthly Performance Trends",
  "reports.trendTooltip": "{pct}% ({n} locs)",
  "reports.trendWeekly": "Weekly Performance Trends",
  "reports.validated": "Validated",
  "reports.viewDetails": "View Details",
  "reports.weekN": "Week {n}",
  "reports.weeklySummary": "Weekly Summary",
  "reports.wk1": "Wk1",
  "reports.wk2": "Wk2",
  "reports.wk3": "Wk3",
  "reports.wk4": "Wk4",
  "schedules.activeRoutes": "Active Routes",
  "schedules.activeSchedules": "Active Schedules",
  "schedules.addNewSchedule": "Add New Schedule",
  "schedules.addSchedule": "Add Schedule",
  "schedules.assignedGuard": "Assigned Guard",
  "schedules.assignedGuards": "Assigned Guards",
  "schedules.calendar": "Calendar",
  "schedules.confirmDelete": "Are you sure you want to delete this schedule?",
  "schedules.coverageGaps": "Coverage Gaps",
  "schedules.createSchedule": "Create Schedule",
  "schedules.dayFri": "Fri",
  "schedules.dayMon": "Mon",
  "schedules.daySat": "Sat",
  "schedules.daySun": "Sun",
  "schedules.dayThu": "Thu",
  "schedules.dayTue": "Tue",
  "schedules.dayWed": "Wed",
  "schedules.daysOfWeek": "Days of Week",
  "schedules.deleteSchedule": "Delete Schedule",
  "schedules.editSchedule": "Edit Schedule",
  "schedules.endTime": "End Time",
  "schedules.fillRequired": "Please fill in all required fields",
  "schedules.frequency": "Frequency",
  "schedules.frequencyCustom": "Custom",
  "schedules.frequencyDaily": "Daily",
  "schedules.frequencyWeekly": "Weekly",
  "schedules.listView": "List View",
  "schedules.morningPatrol": "Morning Patrol",
  "schedules.nightShift": "Night Shift",
  "schedules.route": "Route",
  "schedules.scheduleName": "Schedule Name",
  "schedules.scheduleNamePlaceholder": "e.g., Morning Patrol - Building A",
  "schedules.selectGuard": "Select a guard",
  "schedules.selectRoute": "Select a route",
  "schedules.selectedCount": "Selected: {n} checkpoint(s)",
  "schedules.startTime": "Start Time",
  "schedules.updateSchedule": "Update Schedule",
  "settings.browserDesc": "UA recorded to monitor device integrity.",
  "settings.browserLabel": "Browser",
  "settings.cameraDesc": "Required to scan QR tags. Adjust in system settings if denied.",
  "settings.cameraPermission": "Camera permission",
  "settings.deviceIdDesc": "Fingerprint stored with each scan for audit compliance.",
  "settings.deviceIdLabel": "Device ID",
  "settings.email": "Email",
  "settings.escalationAlerts": "Escalation alerts",
  "settings.fullName": "Full name",
  "settings.installPwa": "Install as PWA",
  "settings.locationDesc": "Required for GPS verification on each checkpoint.",
  "settings.locationPermission": "Location permission",
  "settings.mobilePhone": "Mobile phone",
  "settings.notRegistered": "Not registered yet",
  "settings.notificationDesc": "Control what alerts the supervisor team can send to you.",
  "settings.notificationPreferences": "Notification preferences",
  "settings.permGranted": "Granted",
  "settings.permUnknown": "Unknown",
  "settings.profileDetails": "Profile details",
  "settings.profileSavedMsg": "Profile preferences saved. Changes take effect immediately.",
  "settings.pwaAndroid": "Android (Chrome):",
  "settings.pwaAndroidSteps": "Menu → Install App.",
  "settings.pwaDesktop": "Desktop:",
  "settings.pwaDesktopSteps": "Use browser install prompt in the address bar.",
  "settings.pwaIos": "iOS (Safari):",
  "settings.pwaIosSteps": "Tap Share → Add to Home Screen.",
  "settings.saveProfile": "Save profile",
  "settings.storedLocally": "Settings are stored locally until profile sync is available.",
  "settings.weeklyEmailSummary": "Weekly email summary",
  "users.applicationDetails": "Application details",
  "users.applicationReview": "Application Review:",
  "users.applicationStatus": "Application Status",
  "users.approve": "Approve",
  "users.approveApplication": "Approve Application",
  "users.backToApplications": "Back to Applications",
  "users.colApplicant": "Applicant",
  "users.colRole": "Role",
  "users.dateAdded": "Date added",
  "users.directoryFound": "Found in employees_personal_info table.",
  "users.directoryMatch": "Directory match",
  "users.directoryName": "Directory name",
  "users.email": "Email",
  "users.filterAll": "All Applications",
  "users.filterPending": "Pending Review",
  "users.nextSteps": "Next Steps",
  "users.noApplications": "No applications found",
  "users.phone": "Phone",
  "users.phoneNumber": "Phone number",
  "users.reject": "Reject",
  "users.rejectApplication": "Reject Application",
  "users.requestedRole": "Requested role",
  "users.review": "Review",
  "users.statusRejected": "Rejected",
  "users.stepBackgroundCheck": "Initiate background check",
  "users.stepCheckLicense": "Check license/certification validity",
  "users.stepScheduleInterview": "Schedule interview if approved",
  "users.stepVerifyReferences": "Verify references by phone",
  "users.submitted": "Submitted:",
  "widget.aiSummary": "AI Summary",
  "widget.barChart": "Bar Chart",
  "widget.comparison": "Comparison",
  "widget.dataTable": "Data Table",
  "widget.dayOfWeek": "Day of Week",
  "widget.image": "Image",
  "widget.kpiRow": "KPI Row",
  "widget.lineChart": "Line Chart",
  "widget.metricCard": "Metric Card",
  "widget.pieDonut": "Pie / Donut",
  "widget.rankedTable": "Ranked Table",
  "widget.textBlock": "Text Block",

  // Activity calendar
  "calendar.title": "Patrol Activity Calendar",
  "calendar.subtitle": "Scan activity across every checkpoint, day by day",
  "calendar.viewMonth": "Month",
  "calendar.viewWeek": "Week",
  "calendar.viewDay": "Day",
  "calendar.viewList": "List",
  "calendar.today": "Today",
  "calendar.allHospitals": "All hospitals",
  "calendar.totalScans": "{n} scans",
  "calendar.activeDays": "{n} active days",
  "calendar.alertsCount": "{n} alerts",
  "calendar.noActivityRange": "No scan activity in this period.",
  "calendar.loading": "Loading activity…",
  "calendar.loadFailed": "Failed to load activity",
  "calendar.refresh": "Refresh",
  "calendar.colTime": "Time",
  "calendar.colHospital": "Hospital",
  "calendar.colCheckpoint": "Checkpoint",
  "calendar.colDevice": "Device",
  "calendar.colGuard": "Guard",
  "calendar.violation": "Violation",
  "calendar.violationsCount": "{n} violations",
  "calendar.scansShort": "{n} scans",
  "calendar.hospitalsActive": "{n} hospitals active",
  "calendar.moreEvents": "+{n} more",
  "calendar.weekOf": "Week of {date}",
  "calendar.dayEvents": "Scan events",
  "calendar.hourlyDistribution": "Hourly distribution",
  "calendar.legendLess": "Less",
  "calendar.legendMore": "More",
  "calendar.unknownHospital": "Unknown hospital",
  "calendar.unknownGuard": "Unknown guard",
  "calendar.unknownCheckpoint": "Unknown checkpoint",

  // Checkpoint directory + drill-down history
  "checkpoints.directoryTitle": "Checkpoint Directory",
  "checkpoints.directorySubtitle": "Every registered QR checkpoint, organized by floor",
  "checkpoints.searchPlaceholder": "Search by name, code, or building…",
  "checkpoints.countShown": "{n} checkpoints",
  "checkpoints.exportCsv": "Export CSV",
  "checkpoints.loadingDirectory": "Loading checkpoints…",
  "checkpoints.empty": "No checkpoints found for this hospital.",
  "checkpoints.noFloor": "Unassigned",
  "checkpoints.floorLabel": "Floor {n}",
  "checkpoints.colCode": "Code",
  "checkpoints.colName": "Name",
  "checkpoints.colBuilding": "Building",
  "checkpoints.colFloor": "Floor",
  "checkpoints.colLastScanned": "Last Scanned",
  "checkpoints.colTotalScans": "Total Scans",
  "checkpoints.colStatus": "Status",
  "checkpoints.active": "Active",
  "checkpoints.inactive": "Inactive",
  "checkpoints.never": "Never",
  "checkpoints.untitled": "Untitled Checkpoint",
  "checkpoints.totalScans": "Total Scans",
  "checkpoints.compliance": "Compliance",
  "checkpoints.late": "Late",
  "checkpoints.missed": "Missed",
  "checkpoints.fromDate": "From",
  "checkpoints.toDate": "To",
  "checkpoints.clearDates": "Clear",
  "checkpoints.loadingHistory": "Loading scan history…",
  "checkpoints.noScans": "No scans recorded for this checkpoint in the selected range.",
  "checkpoints.unknownDevice": "Unknown device",
  "checkpoints.noGps": "No GPS",
  "checkpoints.statusOnTime": "On time",
  "checkpoints.statusLate": "Late",
  "checkpoints.statusMissed": "Missed",
  "checkpoints.viewHistory": "View scan history",

  // Patrol compliance
  // NOTE: this view reports checkpoint STATUS. It is not a work assignment and
  // has no route, order or ETA. Keep the wording of these strings consistent
  // with that — see api/compliance/status.ts.
  "compliance.title": "Patrol Compliance",
  "compliance.subtitle": "Checkpoint scan status against each location's configured interval.",
  "compliance.advisory": "Informational only. This view reports when each checkpoint was last scanned and the time remaining in its scan interval. It is not a work assignment, a route, or an instruction. Which checkpoints are scanned and in what order remain the decision of the officer and their supervisor, subject to their employer's post orders and any higher-priority duties.",
  "compliance.loadFailed": "Could not load compliance status",
  "compliance.overdue": "Overdue",
  "compliance.overdueSub": "Past the scan interval",
  "compliance.dueSoon": "Due Soon",
  "compliance.dueSoonSub": "In the last quarter of the interval",
  "compliance.onInterval": "On Interval",
  "compliance.onIntervalSub": "Scanned within the interval",
  "compliance.neverScanned": "No Scan on Record",
  "compliance.neverScannedSub": "Not counted as overdue",
  "compliance.neverScannedNoteTitle": "Why these are not counted as overdue:",
  "compliance.neverScannedNote": "A checkpoint with no recorded scan has no interval to have exceeded. Checkpoints that pre-date compliance tracking read this way until the historical backfill has run for the site, so treating them as overdue would raise an alert for every checkpoint at once.",
  "compliance.openAlerts": "Open Compliance Alerts",
  "compliance.checkpointStatus": "Checkpoint Status",
  "compliance.filterAttention": "Overdue and due soon",
  "compliance.filterOverdue": "Overdue only",
  "compliance.filterAll": "All checkpoints",
  "compliance.emptyAll": "No checkpoints found for this selection.",
  "compliance.emptyAttention": "No checkpoints are overdue or due soon.",
  "compliance.colCheckpoint": "Checkpoint",
  "compliance.colStatus": "Status",
  "compliance.colLastScan": "Last scan",
  "compliance.colInterval": "Interval",
  "compliance.colRemaining": "Time in interval",
  "compliance.colScans24h": "Scans (24h)",
  "compliance.status.OVERDUE": "Overdue",
  "compliance.status.DUE_SOON": "Due Soon",
  "compliance.status.OK": "On Interval",
  "compliance.status.NEVER_SCANNED": "No Scan on Record",
  "compliance.minutesShort": "min",
  "compliance.perDay": "per day",
  "compliance.ruleLocation": "location rule",
  "compliance.ruleHospital": "site default",
  "compliance.truncated": "Showing the most time-critical results only. Narrow by site to see the rest.",
  "compliance.generatedAt": "Status as of",
  "compliance.rules": "Scan Intervals",
  "compliance.rulesTitle": "Scan Intervals",
  "compliance.rulesSubtitle": "A location rule overrides the site default, which overrides the system default.",
  "compliance.rulesEmpty": "No custom rules — the system default applies.",
  "compliance.rulesLoadFailed": "Could not load rules",
  "compliance.rulesSaveFailed": "Could not save rule",
  "compliance.rulesDeleteFailed": "Could not delete rule",
  "compliance.rulesCreateFailed": "Could not create rule",
  "compliance.rulesInvalid": "Interval and daily minimum must be numbers",
  "compliance.systemDefault": "System default",
  "compliance.systemDefaultValue": "every",
  "compliance.systemDefaultNote": "Sites with no rule of their own are measured against this.",
  "compliance.siteWide": "All checkpoints at this site",
  "compliance.colAppliesTo": "Applies to",
  "compliance.colIntervalMin": "Interval (min)",
  "compliance.colMinPerDay": "Min / 24h",
  "compliance.colActions": "Actions",
  "compliance.addSiteDefault": "Add site default",
  "compliance.pickSiteToAdd": "Select a single site to add a default for it.",

  // Officers (patrol staff, not dashboard logins)
  "officers.title": "Officers",
  "officers.subtitle": "Officers who sign on with an N number so their scans are attributed. These are not dashboard logins.",
  "officers.add": "Add officer",
  "officers.addTitle": "Add officer",
  "officers.editTitle": "Edit officer",
  "officers.loadFailed": "Could not load officers",
  "officers.saveFailed": "Could not save officer",
  "officers.deactivateFailed": "Could not deactivate officer",
  "officers.searchPlaceholder": "Search by name, N number or email",
  "officers.filterActive": "Active",
  "officers.filterInactive": "Inactive",
  "officers.filterSuspended": "Suspended",
  "officers.filterPending": "Pending approval",
  "officers.filterRejected": "Rejected",
  "officers.filterAll": "All statuses",
  "officers.onShiftCount": "Currently signed on:",
  "officers.empty": "No officers yet. Add one to start attributing scans.",
  "officers.emptySearch": "No officers match that search.",
  "officers.colOfficer": "Officer",
  "officers.colNNumber": "N number",
  "officers.colSite": "Site",
  "officers.colStatus": "Status",
  "officers.colShift": "Current shift",
  "officers.colActions": "Actions",
  "officers.status.ACTIVE": "Active",
  "officers.status.PENDING": "Pending approval",
  "officers.status.INACTIVE": "Inactive",
  "officers.status.SUSPENDED": "Suspended",
  "officers.status.REJECTED": "Rejected",
  "officers.onShiftSince": "On since",
  "officers.offShift": "Not signed on",
  "officers.scans": "scans",
  "officers.deactivate": "Deactivate",
  "officers.deactivateTitle": "Deactivate officer?",
  "officers.deactivateBody": "{name} will no longer be able to sign on, and any open shift is closed. Their scan history is kept and stays attributed to them.",
  "officers.fieldNNumber": "N number",
  "officers.fieldNNumberHint": "The leading N is added automatically if you omit it.",
  "officers.fieldName": "Full name",
  "officers.fieldSite": "Site",
  "officers.fieldEmail": "Email",
  "officers.fieldPhone": "Phone",
  "officers.fieldStatus": "Status",
  "officers.fieldStatusHint": "Only ACTIVE officers can sign on. Changing away from ACTIVE closes any open shift.",
  "officers.selectSite": "Select a site",
  "officers.formRequired": "N number, name and site are required",
  "officers.pendingTitle": "Pending enrollment approvals",
  "officers.selfEnrolled": "Self-enrolled",
  "officers.enrolledAt": "signed on",
  "officers.enrollDistance": "{d} from the site's recorded scan area",
  "officers.enrollNoGps": "no GPS fix at sign-on",
  "officers.enrollSiteMismatch": "Selected {chosen}, but GPS pointed to {gps} — confirm which is right before approving.",
  "officers.pendingNote": "These identities were self-reported at a badge scan. Their scans attribute provisionally until approved; rejecting keeps the record and its scans for review.",
  "officers.approve": "Approve",
  "officers.reject": "Reject",
  "officers.approveTitle": "Approve enrollment?",
  "officers.approveBody": "Confirms {n} as an officer at {site}. Their pending scans stay attributed and future sign-ons work as normal.",
  "officers.approveName": "Officer name (as it should appear)",
  "officers.approveFailed": "Could not approve enrollment",
  "officers.rejectFailed": "Could not reject enrollment",

  // Patrol reports
  // Reports of the RECORD, for the supervisor's judgement — not rankings,
  // quotas or assignments. Keep wording consistent with the advisory each API
  // response carries (see api/reports/officer-performance.ts).
  "patrolReports.title": "Patrol Reports",
  "patrolReports.subtitle": "Recorded patrol activity by officer, checkpoint coverage, and compliance alert history.",
  "patrolReports.tabOfficers": "Officers",
  "patrolReports.tabCoverage": "Coverage",
  "patrolReports.tabHistory": "Alert History",
  "patrolReports.loadFailed": "Could not load report",
  "patrolReports.badRange": "Start date must be on or before end date",
  "patrolReports.startDate": "Start date",
  "patrolReports.endDate": "End date",
  "patrolReports.totalScans": "Scans in Window",
  "patrolReports.attributed": "Attributed to an Officer",
  "patrolReports.unattributed": "Unattributed",
  "patrolReports.unattributedSub": "Scanned while no officer was signed on",
  "patrolReports.officersListed": "Officers Listed",
  "patrolReports.noOfficers": "No officers in this selection.",
  "patrolReports.colOfficer": "Officer",
  "patrolReports.colSite": "Site",
  "patrolReports.colShifts": "Shifts",
  "patrolReports.colShiftTime": "Shift time",
  "patrolReports.colScans": "Scans",
  "patrolReports.colCheckpoints": "Checkpoints",
  "patrolReports.colScansHr": "Scans / hr",
  "patrolReports.colLastScan": "Last scan",
  "patrolReports.fullyCovered": "Covered Every Day",
  "patrolReports.partiallyCovered": "Covered Some Days",
  "patrolReports.uncovered": "No Scans in Window",
  "patrolReports.of": "of",
  "patrolReports.coverageTitle": "Checkpoint Coverage",
  "patrolReports.coverageSort": "Least-covered checkpoints first.",
  "patrolReports.colCheckpoint": "Checkpoint",
  "patrolReports.colDaysCovered": "Days covered",
  "patrolReports.colExpected": "Expected",
  "patrolReports.colStatusNow": "Status now",
  "patrolReports.alertsRaised": "Alerts in Window",
  "patrolReports.alertsOpen": "Still Open",
  "patrolReports.alertsResolved": "Resolved",
  "patrolReports.repeatTitle": "Checkpoints Raising Alerts Most Often",
  "patrolReports.colAlerts": "Alerts",
  "patrolReports.colOpen": "Open",
  "patrolReports.colWorst": "Worst overdue",
  "patrolReports.colAvgResolve": "Avg. to resolve",
  "patrolReports.colLatest": "Latest",
  "patrolReports.alertLog": "Alert Log",
  "patrolReports.noAlerts": "No alerts in this window.",
  "patrolReports.resolvedIn": "resolved in",
  "patrolReports.stateResolved": "Resolved",
  "patrolReports.stateOpen": "Open",

  // Email alert preferences (Alerts page)
  "emailPrefs.title": "Email Alert Preferences",
  "emailPrefs.subtitle": "Choose which compliance emails you receive, per site.",
  "emailPrefs.loadFailed": "Could not load email preferences",
  "emailPrefs.saveFailed": "Could not save preference",
  "emailPrefs.noSites": "No sites in your access list.",
  "emailPrefs.noPreference": "No preference saved — fallback applies",
  "emailPrefs.explainer": "Sites where nobody has saved a preference fall back to emailing all supervisors and directors. Saving any preference at a site switches it to explicit choices only.",
  "emailPrefs.overdue": "Overdue alerts",
  "emailPrefs.overdueHint": "Immediate email when checkpoints pass their scan interval",
  "emailPrefs.daily": "Daily digest",
  "emailPrefs.dailyHint": "One compliance summary per day",
  "emailPrefs.officerSummary": "Officer activity summary",
  "emailPrefs.officerSummaryHint": "Weekly recorded-activity report. Off unless you opt in.",
  "emailPrefs.manageOthers": "Manage recipients at this site",
  "emailPrefs.noOthers": "No other recipients configured here.",
  "emailPrefs.enrollEmail": "email@emoryhealthcare.org",
  "emailPrefs.enrollName": "Name (optional)",
  "emailPrefs.enroll": "Add recipient",
  "emailPrefs.officerSummaryNote": "The officer activity summary is a scheduled report of recorded scan activity with its caveats included — it is not a performance score, and it is never sent to anyone who has not opted in.",

  // Officer scan-history map
  "officerMap.open": "View scan history map",
  "officerMap.title": "Scan History",
  "officerMap.loadFailed": "Could not load scan history",
  "officerMap.empty": "No GPS-tagged scans attributed to this officer in the selected window.",
  "officerMap.firstScan": "First scan",
  "officerMap.lastScan": "Last scan",
  "officerMap.scans": "scans",
  "officerMap.ofTotal": "of {n} scans",
  "officerMap.note": "Only scans attributed to this officer appear here. Scans made while not signed on are unattributed and not shown.",
  "patrolReports.officersTableTitle": "Recorded Activity by Officer",

  // Officer detail page
  "officerDetail.allOfficers": "All Officers",
  "officerDetail.backToOfficers": "Back to Officers",
  "officerDetail.notFound": "Officer not found.",
  "officerDetail.loadFailed": "Failed to load officer",
  "officerDetail.selfEnrolled": "Self-enrolled",
  "officerDetail.addedBySupervisor": "Added by supervisor",
  "officerDetail.memberSince": "member since {date}",
  "officerDetail.totalScans": "Total scans",
  "officerDetail.shifts": "Shifts",
  "officerDetail.activeDays": "Active days",
  "officerDetail.avgShift": "Avg / shift",
  "officerDetail.noShiftsYet": "no shifts yet",
  "officerDetail.scansPerShift": "scans per shift",
  "officerDetail.lastActive": "Last active",
  "officerDetail.whereScanned": "Where {name} Scanned",
  "officerDetail.mapToLight": "Switch map to light tiles",
  "officerDetail.mapToDark": "Switch map to dark tiles",
  "officerDetail.light": "Light",
  "officerDetail.dark": "Dark",
  "officerDetail.noGpsScans": "No GPS-tagged scans to map in the last 30 days.",
  "officerDetail.scanActivityTitle": "Scan Activity — Last {n} Days",
  "officerDetail.noScansInDays": "No scans recorded in the last {n} days.",
  "officerDetail.scanHistory": "Scan History",
  "officerDetail.searchScans": "Search scans…",
  "officerDetail.colDateTime": "Date & Time",
  "officerDetail.colLocation": "Location",
  "officerDetail.colHospital": "Hospital",
  "officerDetail.colCoordinates": "Coordinates",
  "officerDetail.noScansMatch": "No scans match “{q}”.",
  "officerDetail.showingScans": "Showing {shown} of {total} recorded scans (last 30 days).",
  "officerDetail.timelineRange": "{n} scans · oldest → newest",
  "officerDetail.hoverTimeline": "Hover the timeline to trace a scan",
  "officerDetail.timelineCaption": "Each dot is one scan, placed by when it happened and colored oldest-blue to newest-red. Hover to highlight it on the map. No path between scans is drawn — only recorded scan locations are shown.",

  // Scan Map — officer filter, misplaced & anomaly views, popups
  "map.officer": "Officer",
  "map.clearOfficer": "Clear officer filter",
  "map.searchOfficer": "Search officer…",
  "map.misplacedTitle": "Show only scans whose GPS landed more than 1km from their checkpoint",
  "map.misplacedOn": "Misplaced",
  "map.misplacedOff": "Misplaced",
  "map.anomaliesTitle": "Show scans flagged as anomalous — impossible officer travel, or a scan plotted far from its own hospital",
  "map.anomalies": "Anomalies",
  "map.popupOfficer": "Officer:",
  "map.anomalousMovement": "Anomalous — impossible travel between this officer's scans",
  "map.anomalousHospital": "Anomalous — plotted far from its labeled hospital",
  "map.anomalousBoth": "Anomalous — impossible travel and far from its hospital",
  "map.metersFromCheckpoint": "{n} m from its checkpoint",
  "map.scansLabel": "scans",
  "map.earliest": "Earliest",
  "map.latest": "Latest",
};

export type TranslationKey = keyof typeof en;
export default en;
